import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { sessionState, threadHistoryState } from '@chainlit/react-client';

import { animatingThreadIdState } from './animatingThreadIdState';

/**
 * Hook that listens for title_generated socket events from the backend.
 * When a title is generated, it updates the thread name in state and
 * triggers the typewriter animation for that thread.
 */

export function useTitleGeneratedListener() {
    const session = useRecoilValue(sessionState);
    const setThreadHistory = useSetRecoilState(threadHistoryState);
    const setAnimatingThreadId = useSetRecoilState(animatingThreadIdState);

    useEffect(() => {
        if (!session?.socket) return;

        const handleTitleGenerated = (event: {
            title: string;
            thread_id: string;
        }) => {
            // Update the thread name in state
            setThreadHistory((prev) => {
                const existingThreads = prev?.threads || [];
                const existingIndex = existingThreads.findIndex(
                    (t) => t.id === event.thread_id
                );

                if (existingIndex >= 0) {
                    const updatedThreads = [...existingThreads];
                    updatedThreads[existingIndex] = {
                        ...updatedThreads[existingIndex],
                        name: event.title
                    };
                    return {
                        ...prev,
                        threads: updatedThreads
                    };
                }
                return prev;
            });

            setAnimatingThreadId(event.thread_id);
        };

        session.socket.on('title_generated', handleTitleGenerated);

        return () => {
            session.socket.off('title_generated', handleTitleGenerated);
        };
    }, [session?.socket, setThreadHistory, setAnimatingThreadId]);
}
